import { type CSSObject, type Theme } from '@mui/material'
import Typography from '../../Typography'
import { styled } from '../../../utils/styled'
import { type AvatarKind, type Size } from './types'
import { type CSSProperties } from 'react'

export const SMALL_AVATAR_WIDTH_PX = 24
export const MEDIUM_AVATAR_WIDTH_PX = 40
export const LARGE_AVATAR_WIDTH_PX = 64

export const getSizedStyles = (
  size: Size,
  theme: Theme
): { width: string; height: string; borderRadius: string | undefined; p: { fontSize: string } } => {
  switch (size) {
    case 'small':
      return {
        width: `${SMALL_AVATAR_WIDTH_PX}px`,
        height: `${SMALL_AVATAR_WIDTH_PX}px`,
        borderRadius: theme.borderRadius.xSmall,
        p: {
          fontSize: '0.5em'
        }
      }
    case 'medium':
      return {
        width: `${MEDIUM_AVATAR_WIDTH_PX}px`,
        height: `${MEDIUM_AVATAR_WIDTH_PX}px`,
        borderRadius: theme.borderRadius.medium,
        p: {
          fontSize: '0.875em'
        }
      }
    case 'large':
      return {
        width: `${LARGE_AVATAR_WIDTH_PX}px`,
        height: `${LARGE_AVATAR_WIDTH_PX}px`,
        borderRadius: theme.borderRadius.xLarge,
        p: {
          fontSize: '1.25em'
        }
      }
  }
}

const getKindBackgroundColor = (kind: AvatarKind, theme: Theme): string | undefined => {
  switch (kind) {
    case 'image':
      return theme.tokens.background.main.default
    case 'label':
      return theme.tokens.border.focus.brand.halo
    case 'number':
      return theme.tokens.background.brand.main.default
  }
}

const getDisabledBackgroundColor = (kind: AvatarKind, theme: Theme, backgroundColor?: string): string | undefined => {
  if (backgroundColor !== undefined) {
    return backgroundColor
  }

  if (kind === 'image') {
    return getKindBackgroundColor(kind, theme)
  }

  return theme.tokens.background.tonal.soft.pressed
}

export const StyledImage = styled('img')<{ $objectFit?: CSSProperties['objectFit'] }>(({ $objectFit: objectFit }) => ({
  width: '100%',
  height: '100%',
  objectFit: objectFit ?? 'fill'
}))

export const StyledAvatar = styled('div')<{
  $size: Size
  $kind: AvatarKind
  $backgroundColor?: string
  $disabled: boolean
}>(
  ({ $size: size, $kind: kind, theme, $backgroundColor: backgroundColor, $disabled: disabled }): CSSObject => ({
    backgroundColor: disabled
      ? getDisabledBackgroundColor(kind, theme, backgroundColor)
      : backgroundColor ?? getKindBackgroundColor(kind, theme),
    border: backgroundColor !== undefined && !disabled ? 'none' : `1px solid ${theme.tokens.border.main.tonal}`,
    overflow: 'hidden',
    flexShrink: 0,
    opacity: disabled && kind === 'image' ? 0.5 : 1,
    ...getSizedStyles(size, theme)
  })
)

export const StyledLabel = styled(Typography)<{ $kind?: AvatarKind; $disabled?: boolean }>(
  ({ theme, $kind: kind, $disabled: disabled }): CSSObject => ({
    color:
      disabled === true
        ? theme.tokens.foreground.main.disabled
        : kind === 'number'
        ? theme.tokens.foreground.main.staticWhite
        : theme.tokens.foreground.main.primary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    textTransform: 'uppercase',
    ...theme.typography.h3
  })
)
